import { useCallback, useEffect, useMemo } from 'react';
import {
  Navigate,
  Route,
  Routes,
  useNavigate,
  useParams,
} from 'react-router-dom';
import {
  ModalManager,
  ModalManagerProvider,
  useModalManager,
} from '@cotera/client/app/components/app/modal-manager';
import { CommandPalette } from '@cotera/client/v0/actions/command-palette/command-palette';
import {
  NavigatorProvider,
  type Navigator,
} from '@cotera/client/v0/actions/command-palette/navigator';
import {
  Actions,
  ModelScopeFactory,
} from '@cotera/client/v0/actions/framework/context';
import { SuspensefulStorageProvider } from '@cotera/client/app/etc/storage/provider';
import { useDatasetFilterScope } from '../dataset/preview/filter-scope';
import { PortalContainerProvider } from '../../components/portal-container';
import {
  SidebarInset,
  SidebarProvider,
} from '@cotera/client/v0/components/ui/sidebar';
import {
  FallbackPageSidebarTrigger,
  SidebarTriggerPresenceProvider,
} from '@cotera/client/v0/components/ui/page-bar-sidebar-trigger';
import {
  useTenantedClient,
  useUntenantedClient,
  useWhoami,
} from '../../stores/org';
import { useAnalytics } from '../../etc/index';
import { useQueryClient } from '@tanstack/react-query';
import { SwitchAppVersionAction } from '../etc/switch-app-version.action';
import { OpenSettings } from '@cotera/client/v0/actions/tabs/open-settings.action';
import { NewChatAction } from '../chat/actions/new-chat.action';
import { NewAgentAction } from '../agent/new-agent.action';
import { OpenAgentChatAction } from '../chat/actions/open-agent-chat.action';
import { OpenWorkflowChatAction } from '../chat/actions/open-workflow-chat.action';
import { useEagerQuery } from '../../hooks/use-eager-suspense-query';
import { queryKeys } from '../../hooks/query-cache-keys';
import { Assert } from '@cotera/utilities';
import { CocoSidebar } from './sidebar/coco-sidebar';
import { CocoChatNew } from '../chat/pages/coco-chat-new';
import { ChatHistoryPage } from '../chat/pages/chat-history-page';
import { CocoChat } from '../chat/pages/coco-chat';
import { AgentPage } from '../agent/agent.page';
import { DocumentPage } from '../document/document.page';
import { DatasetPage } from '../dataset/dataset.page';
import { UploadCsvDatasetAction } from '../dataset/upload-csv-dataset.action';
import { CreateDatasetAction } from '../dataset/create-dataset.action';
import { CreateWarehouseDatasetAction } from '../dataset/create-warehouse-dataset.action';
import { AddDatasetAction } from '../dataset/add-dataset.action';
import { NewDocumentAction } from '../document/new-document.action';
import { FolderContentsPage } from '../folder/folder-contents.page';
import { TriggersPage, WorkflowsTriggersPage } from '../trigger/workflows.page';
import { TriggersNewPage } from '../trigger/triggers-new.page';
import { TriggerDetailPage } from '../trigger/trigger-detail.page';
import { NewTriggerAction } from '../trigger/new-trigger.action';
import { CreateStandaloneTriggerAction } from '../trigger/create-standalone-trigger.action';
import { WorkflowPage } from '../workflow/workflow.page';
import { WorkflowRunsPage } from '../workflow/workflow-runs.page';
import { WorkflowRunDetailPage } from '../workflow/workflow-run-detail.page';
import { NewWorkflowAction } from '../workflow/new-workflow.action';
import { RenameWorkflowAction } from '../workflow/rename-workflow.action';
import { RenameTriggerAction } from '../trigger/rename-trigger.action';
import { SaveWorkflowAction } from '../workflow/save-workflow.action';
import { CancelWorkflowRunsAction } from '../workflow/cancel-workflow-runs.action';
import { RetryWorkflowRunsAction } from '../workflow/retry-workflow-runs.action';
import { ShowDeveloperDetailsAction } from '@cotera/client/v0/actions/developer-details/show-developer-details.action';
import { AppModel } from './app.model';
import { CocoBroadcastQuerySyncModel } from './broadcast-query-sync.model';
import { StopAction } from '../chat/actions/coco-stop.action';
import { FocusAgentAction } from '../chat/actions/coco-focus-agent.action';
import { UnfocusAgentAction } from '../chat/actions/coco-unfocus-agent.action';
import { AddResourceArtifactAction } from '../chat/actions/coco-add-resource-artifact.action';
import { RemoveResourceArtifactAction } from '../chat/actions/coco-remove-resource-artifact.action';
import { RemoveLoadedToolAction } from '../chat/actions/coco-remove-loaded-tool.action';
import { AddCreditsAction } from '../etc/add-credits.action';
import { CreateFolderAction } from '../resources/actions/create-folder.action';
import {
  ResourceNavigatorModel,
  usePageResourceNavigator,
} from '../resources/navigation';
import { MoveToFolderAction } from '../resources/actions/move-to-folder.action';
import { RenameFolderAction } from '../resources/actions/rename-folder.action';
import { RenameChatAction } from '../resources/actions/rename-chat.action';
import { focusedArtifactIdFromChatSplat } from '../chat/artifacts/artifact-focus-view-route';
import { RenameDatasetAction } from '../dataset/rename-dataset.action';
import { SetDatasetRowKeysAction } from '../dataset/set-dataset-row-keys.action';
import { RetryFailedDatasetRunsAction } from '../dataset/retry-failed-dataset-runs.action';
import { ExpandWorkflowsAction } from '../dataset/expand-workflows.action';
import { ReuploadCsvDatasetAction } from '../dataset/reupload-csv-dataset.action';
import { DetectChangesAction } from '../dataset/detect-changes.action';
import { SetChangeDetectionScheduleAction } from '../dataset/set-change-detection-schedule.action';
import { SetWriteBackTableAction } from '../dataset/set-write-back-table.action';
import { FlushWarehouseAction } from '../dataset/flush-warehouse.action';
import { SaveDatasetAction } from '../dataset/save-dataset.action';
import { RunDatasetPreviewQueryAction } from '../dataset/run-dataset-preview-query.action';
import { ViewCategoryBreakdownAction } from '../dataset/preview/view-category-breakdown.action';
import { ArchiveFolderAction } from '../folder/archive-folder.action';
import {
  ArchiveResourceAction,
  type ArchiveResourceActionOptions,
} from '../resources/actions/archive-resource.action';
import { AgentModel } from '../agent/agent.model';
import { useFeatureFlagEnabled } from '../../hooks/use-feature-flag';
import { ContentScrollProvider } from './content-scroll-context';
import { SwitchCocoModelAction } from '../settings/switch-coco-model.action';
import { ShareLinkActivatePage } from './share-link-activate.page';
import { ShareResourceLinkAction } from '../resources/actions/share-resource-link.action';
import { ProtectedRoute } from '../../components/app/permissioned/index';
import { AtcVisibilityPage } from '@cotera/client/v0/pages/atc-visibility';
import { BillEngineeringHoursPage } from '@cotera/client/v0/pages/bill-engineering-hours';
import { MaterializationIde } from '../../pages/ide/materialization';
import { Artifacts } from '../../pages/ide/artifacts/list';
import { ViewArtifact } from '../../pages/ide/artifacts/view-artifact';
import { DesignSystemPage } from './design-system.page';
import { isProduction } from '../../etc/utils';
import { RunToolAction } from '../chat/actions/coco-run-tool.action';
import { SubmitToolAction } from '../chat/actions/coco-submit-tool.action';
import {
  CollapseArtifactPanelAction,
  ExpandArtifactPanelAction,
} from '@cotera/client/v0/chat/actions';
import { useWatchablesFactory } from '../watchables/watchables-factory-context';
import { ToggleFavoriteAction } from '../resources/actions/toggle-favorite.action';
import { LocalStorageConnection } from '@cotera/client/v0/models/user-branches';

export const V1AppShell: React.FC = () => {
  // Surfaces portaled to document.body (sheets, dropdowns) and the page
  // behind the shell sit outside .v1-app-shell, so they'd otherwise pick up
  // the enterprise (v0) tokens. Tag the root element while the Coco shell is
  // mounted so v1.css can theme those outside-shell surfaces without leaking
  // Coco colors into the v0 app's global :root tokens.
  useEffect(() => {
    document.documentElement.classList.add('v1-theme');
    return () => {
      document.documentElement.classList.remove('v1-theme');
    };
  }, []);

  return (
    <div className="v1-app-shell relative">
      <svg className="pointer-events-none absolute inset-0 w-full h-full">
        <filter id="paper-texture">
          <feTurbulence
            type="fractalNoise"
            baseFrequency="0.9"
            numOctaves="4"
          />
          <feColorMatrix
            values="0 0 0 0 0.95
                    0 0 0 0 0.95
                    0 0 0 0 0.95
                    0 0 0 0.05 0"
          />
        </filter>
        <rect width="100%" height="100%" filter="url(#paper-texture)" />
      </svg>
      <PortalContainerProvider>
        {/* v1 had no storage provider at all, so `useStorage()` returned null
            everywhere under this shell and every surface reading it silently
            fell back to holding its state in memory. For the dataset grid that
            meant filters, sorts, column widths, pinning, order and row height
            were all dropped the moment the grid remounted — which is what
            opening the focus view does. v0 has mounted this since it was
            written; only the newer shell was missing it. */}
        <SuspensefulStorageProvider>
          <ModalManagerProvider>
            <SidebarProvider className="v1-typography">
              <V1AppShellContent />
            </SidebarProvider>
          </ModalManagerProvider>
        </SuspensefulStorageProvider>
      </PortalContainerProvider>
    </div>
  );
};

const V1AppShellContent: React.FC = () => {
  // Watched here because the grid cannot tell leaving from remounting: it sees
  // an unmount either way. See `useDatasetFilterScope`.
  useDatasetFilterScope();
  const modalManager = useModalManager((s) => s.actions);
  const queryClient = useQueryClient();
  const watchablesFactory = useWatchablesFactory();
  const navigate = useNavigate();
  const org = useWhoami((s) => s.org);
  const userId = useWhoami((s) => s.whoami.id);
  const client = useTenantedClient();
  const untenantedClient = useUntenantedClient();
  const analytics = useAnalytics();
  const storage = useMemo(() => new LocalStorageConnection(org.id), [org.id]);
  // The app-level surface for opening resources. Artifact panels push their own
  // navigator onto ResourceNavigatorModel while mounted; this is what's left
  // when none is.
  const pageResourceNavigator = usePageResourceNavigator();
  const createModels = useCallback(
    () => [
      new ResourceNavigatorModel(pageResourceNavigator),
      new AppModel({
        orgId: org.id,
        userId,
        client,
        untenantedClient,
        watchablesFactory,
        storage,
      }),
      new CocoBroadcastQuerySyncModel({
        orgId: org.id,
        queryClient,
        watchablesFactory,
      }),
    ],
    [
      client,
      untenantedClient,
      org.id,
      userId,
      queryClient,
      watchablesFactory,
      storage,
      pageResourceNavigator,
    ]
  );

  const isCocoModelSwitchEnabled = useFeatureFlagEnabled('coco-is-kimi');
  const hasLegacyAppAccess = useFeatureFlagEnabled('legacy-app-access');
  const datasetsEnabled = useFeatureFlagEnabled('datasets');

  const archiveResourceOptions: ArchiveResourceActionOptions = useMemo(
    () => ({
      getAskSubtitle: (ctx) => {
        const agent = ctx.targetedModelOfType(AgentModel);
        if (agent === null || agent.isNew) {
          return undefined;
        }
        const hasUnsaved = agent.hasUnsavedChangesWatchable.snapshot();
        return hasUnsaved
          ? 'Archive this agent? Your unsaved changes will be lost. Are you sure?'
          : undefined;
      },
      navigate,
      orgSlug: org.slug,
    }),
    [navigate, org.slug]
  );

  const actions = useMemo(
    () => [
      ...(isCocoModelSwitchEnabled
        ? [new SwitchCocoModelAction(modalManager)]
        : []),
      ...(hasLegacyAppAccess
        ? [new SwitchAppVersionAction(modalManager, 'v1', userId)]
        : []),
      new NewChatAction(pageResourceNavigator, client.resource.dataset),
      new NewAgentAction(navigate, org.slug),
      new OpenAgentChatAction(client.resource, navigate, org.slug),
      new OpenWorkflowChatAction(client.resource, navigate, org.slug),
      new NewDocumentAction(pageResourceNavigator),
      new AddDatasetAction(
        new CreateDatasetAction(pageResourceNavigator),
        new UploadCsvDatasetAction(client, pageResourceNavigator, modalManager),
        new CreateWarehouseDatasetAction(client, pageResourceNavigator),
        modalManager
      ),
      new NewTriggerAction(modalManager, navigate, org.slug),
      new CreateStandaloneTriggerAction(client, org.id, (event, properties) =>
        analytics.track(event, properties)
      ),
      new NewWorkflowAction(navigate, org.slug),
      new RenameWorkflowAction(client, modalManager, org.id),
      new RenameTriggerAction(client, modalManager, org.id),
      new SaveWorkflowAction(queryClient, modalManager),
      new CancelWorkflowRunsAction(client, modalManager),
      new RetryWorkflowRunsAction(client),
      new ShowDeveloperDetailsAction(modalManager),
      new OpenSettings(modalManager),
      new StopAction(client.resource.chat),
      new FocusAgentAction(client.resource.chat),
      new UnfocusAgentAction(client.resource.chat),
      new AddResourceArtifactAction(client.resource.chat),
      new RemoveResourceArtifactAction(client.resource.chat),
      new RemoveLoadedToolAction(client.resource.chat),
      new AddCreditsAction(client, queryClient, org.id, modalManager),
      new CreateFolderAction(
        client,
        queryClient,
        org.id,
        modalManager,
        pageResourceNavigator
      ),
      new MoveToFolderAction(client, queryClient, org.id, modalManager),
      new ArchiveResourceAction(
        client,
        queryClient,
        org.id,
        modalManager,
        archiveResourceOptions
      ),
      new RenameFolderAction(client, modalManager),
      new RenameChatAction(client, queryClient, org.id, modalManager),
      new ToggleFavoriteAction(client, queryClient, org.id),
      new RenameDatasetAction(client, modalManager, org.id),
      new SetDatasetRowKeysAction(client, modalManager, org.id),
      new RetryFailedDatasetRunsAction(client),
      new ExpandWorkflowsAction(client, modalManager, org.id),
      new ReuploadCsvDatasetAction(client, modalManager, org.id),
      new DetectChangesAction(client),
      new SetChangeDetectionScheduleAction(client, modalManager, org.id),
      new SetWriteBackTableAction(client, modalManager, org.id),
      new FlushWarehouseAction(client),
      new SaveDatasetAction(client, org.id),
      new RunDatasetPreviewQueryAction(),
      new ViewCategoryBreakdownAction(modalManager),
      new ShareResourceLinkAction(modalManager),
      new ArchiveFolderAction(
        client.resource,
        queryClient,
        navigate,
        org.slug,
        org.id,
        modalManager
      ),
      new RunToolAction(client),
      new SubmitToolAction(client.resource.chat),
      new ExpandArtifactPanelAction(),
      new CollapseArtifactPanelAction(),
    ],
    [
      analytics,
      client,
      isCocoModelSwitchEnabled,
      hasLegacyAppAccess,
      modalManager,
      navigate,
      org.id,
      org.slug,
      queryClient,
      archiveResourceOptions,
      userId,
      pageResourceNavigator,
    ]
  );

  useEagerQuery(queryKeys.toolsV2.all({ orgId: org.id }), async () => {
    return Assert.assertOk(await client.toolsV2.list({}));
  });
  useEagerQuery(
    queryKeys.settings.billing.billingSettingsInfo({ orgId: org.id }),
    async () => {
      return await client.billingSettings.getBillingSettingsInfo({});
    }
  );

  // Create navigator that uses react-router with org slug prefix
  const navigator: Navigator = useMemo(
    () => (route: string) => {
      // tabRoute in v0 is typically a path like /dataset/:id which is relative to org slug
      // We need to prefix it with the org slug for react-router navigation
      const fullRoute = `/${org.slug}${route}`;
      navigate(fullRoute);
    },
    [navigate, org.slug]
  );

  return (
    <Actions actions={actions}>
      <ModelScopeFactory
        createModels={createModels}
        deps={[org.id, client, queryClient]}
      >
        {/* Wraps the whole shell, not just `CommandPalette` — that component
            renders null, and the palette view it opens is mounted by
            `ModalManager` below. Scoping the provider to it left every modal
            reading a null navigator, so clicking a search result went
            nowhere. */}
        <NavigatorProvider navigator={navigator}>
          <>
            <CommandPalette />
            <CocoSidebar />
            {/* `h-dvh`, not `h-screen`. `100vh` on mobile browsers is the
                viewport with the URL bar *retracted*, so at rest the shell was
                taller than the visible area — and since it also clips its own
                overflow, whatever sat at the bottom (the chat composer, a
                list's last row) was pushed under the browser chrome with no
                way to scroll to it. The dynamic unit tracks the chrome as it
                shows and hides. Identical to `100vh` on desktop. */}
            <SidebarInset className="bg-transparent h-dvh overflow-hidden flex flex-col">
              {/* The nav has to be reachable from every screen, and on mobile
                  the trigger in a page's own bar is the only way to it. Plenty
                  of screens never reach that bar — a chat suspended on its
                  tools, a page bailing to `null` on a loading flag, the
                  new-trigger and share-activation pages, which have no bar by
                  design — and on a phone each of those was a dead end. The
                  provider watches whether the route below drew one; the bar
                  above fills in when it did not, and renders nothing when it
                  did. See `SidebarTriggerPresenceProvider`. */}
              <SidebarTriggerPresenceProvider>
                <FallbackPageSidebarTrigger />
                {/* The routes get their own box under the strip above, because
                    every page sizes itself with `h-full` — 100% of whatever
                    contains it. As a bare sibling of the strip that resolved to
                    the full inset, so on the pages where the strip does appear
                    the page ran 44px past the bottom of a window that clips its
                    overflow, taking the composer with it. This takes the
                    remaining height and gives the pages something true to
                    measure against. */}
                <div className="flex min-h-0 flex-1 flex-col">
                  <ContentScrollProvider>
                    <Routes>
                      <Route
                        path={`/${org.slug}/agent/:id`}
                        element={<AgentPage />}
                      />
                      {/* Agents, documents and datasets are browsed from the
                  Library, which carries a lens per type. Their standalone list
                  pages outlived that move and stayed reachable by URL, showing
                  the same resources under chrome nothing in the sidebar points
                  at — so each list now redirects to the Library lens that
                  replaced it, the way /shared already does. The item routes
                  above are untouched: those are the deep links. */}
                      <Route
                        path={`/${org.slug}/agents`}
                        element={
                          <Navigate
                            to={`/${org.slug}/folders?type=agent`}
                            replace
                          />
                        }
                      />
                      <Route
                        path={`/${org.slug}/triggers/new`}
                        element={<TriggersNewPage />}
                      />
                      <Route
                        path={`/${org.slug}/triggers/:triggerId`}
                        element={<TriggerDetailPage />}
                      />
                      <Route
                        path={`/${org.slug}/triggers`}
                        element={<TriggersPage />}
                      />
                      <Route
                        path={`/${org.slug}/workflows/:workflowId/runs/:runId`}
                        element={<WorkflowRunDetailPage />}
                      />
                      <Route
                        path={`/${org.slug}/workflows/:workflowId/runs`}
                        element={<WorkflowRunsPage />}
                      />
                      <Route
                        path={`/${org.slug}/workflows/:workflowId`}
                        element={<WorkflowPage />}
                      />
                      <Route
                        path={`/${org.slug}/workflows`}
                        element={<WorkflowsTriggersPage />}
                      />
                      <Route
                        path={`/${org.slug}/documents/:documentId`}
                        element={<DocumentPage />}
                      />
                      <Route
                        path={`/${org.slug}/documents`}
                        element={
                          <Navigate
                            to={`/${org.slug}/folders?type=document`}
                            replace
                          />
                        }
                      />
                      {datasetsEnabled ? (
                        <>
                          <Route
                            path={`/${org.slug}/datasets/:datasetId`}
                            element={<DatasetPage />}
                          />
                          <Route
                            path={`/${org.slug}/datasets`}
                            element={
                              <Navigate
                                to={`/${org.slug}/folders?type=dataset`}
                                replace
                              />
                            }
                          />
                        </>
                      ) : null}
                      {/* One route for the chat and for focus on one of its
                    artifacts. Two would swap elements and remount the whole
                    tree; see {@link ARTIFACT_FOCUS_VIEW_SEGMENT}. */}
                      <Route
                        path={`/${org.slug}/chats/:chatId/*`}
                        element={<CocoChatRoute />}
                      />
                      <Route
                        path={`/${org.slug}/chats`}
                        element={<ChatHistoryPage />}
                      />
                      <Route
                        path={`/${org.slug}/share/:token`}
                        element={<ShareLinkActivatePage />}
                      />
                      <Route
                        path={`/${org.slug}/folders/:folderId`}
                        element={<FolderContentsPage />}
                      />
                      <Route
                        path={`/${org.slug}/folders`}
                        element={<FolderContentsPage />}
                      />
                      {/* "Shared with me" is now the Library's ownership lens; keep the
                  old route working by redirecting to the Library with that lens
                  applied so existing /shared links land on the same view. */}
                      <Route
                        path={`/${org.slug}/shared`}
                        element={
                          <Navigate
                            to={`/${org.slug}/folders?ownership=shared`}
                            replace
                          />
                        }
                      />
                      <Route
                        element={
                          <ProtectedRoute scopes={[['use', 'superadmin']]} />
                        }
                      >
                        <Route
                          path={`/${org.slug}/atc-visibility`}
                          element={<AtcVisibilityPage />}
                        />
                        <Route
                          path={`/${org.slug}/bill-engineering-hours`}
                          element={<BillEngineeringHoursPage />}
                        />
                        <Route
                          path={`/${org.slug}/materializations`}
                          element={<MaterializationIde />}
                        />
                        <Route
                          path={`/${org.slug}/artifacts`}
                          element={<Artifacts />}
                        />
                        <Route
                          path={`/${org.slug}/artifacts/:artifactId`}
                          element={<ViewArtifact />}
                        />
                      </Route>
                      {!isProduction() && (
                        <Route
                          path={`/${org.slug}/design-system`}
                          element={<DesignSystemPage />}
                        />
                      )}
                      <Route path="*" element={<CocoChatNew />} />
                    </Routes>
                  </ContentScrollProvider>
                </div>
              </SidebarTriggerPresenceProvider>
            </SidebarInset>
            <ModalManager />
          </>
        </NavigatorProvider>
      </ModelScopeFactory>
    </Actions>
  );
};

/**
 * A chat, and — when its address names one — the artifact it is focused on.
 *
 * One component for both, because `CocoChat` already treats focus as an
 * optional prop rather than a different page: it keeps the conversation load,
 * the artifact list, the access gate and every renderer's model scope, and
 * draws one half of that tree or the other. Splitting it across two routes is
 * what used to tear it down between them.
 */
const CocoChatRoute: React.FC = () => {
  const { chatId, '*': splat } = useParams<{ chatId: string; '*': string }>();
  const focusedArtifactId = focusedArtifactIdFromChatSplat(splat);
  return (
    <CocoChat
      conversationId={chatId!}
      {...(focusedArtifactId !== null && {
        focusViewArtifactId: focusedArtifactId,
      })}
    />
  );
};
